/**
 * Purchase Order panel (P4) — raise / send / track POs for one plan stage's
 * procurement. Vendor picker is ranked (preferred → cheapest → fastest) from the
 * package's BOQ materials; lines are seeded from the package BOQ. Send generates
 * the signed PO PDF and emails the vendor; delivery + received quantities are
 * tracked here and feed the DELIVERY-sub-stage tasks.
 */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ChevronDown, ChevronRight, FileText, Plus, Trash2, Send, Truck, Star, Pencil } from "lucide-react";
import { OutlineButton, PrimaryButton } from "../../components/Buttons";
import AsyncButton from "../../components/AsyncButton";
import {
  getVendorOptions, getPurchaseOrders, raisePurchaseOrder, updatePurchaseOrder, sendPurchaseOrder, getPoPrefill,
} from "../../services/interiorExecution";

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
// A vendor's per-item lead time → a suggested expected date (today + lead days), as yyyy-mm-dd.
const dateFromLead = (days) =>
  days != null && days !== "" ? new Date(Date.now() + Number(days) * 86400000).toISOString().slice(0, 10) : "";

const PO_STATUS = {
  DRAFT: "bg-slate-100 text-slate-600",
  SENT: "bg-sky-50 text-sky-700",
  ACKNOWLEDGED: "bg-indigo-50 text-indigo-700",
  PARTIALLY_DELIVERED: "bg-amber-50 text-amber-700",
  DELIVERED: "bg-emerald-50 text-emerald-700",
  CLOSED: "bg-slate-100 text-slate-500",
  CANCELLED: "bg-rose-50 text-rose-600",
};

const PurchaseOrderPanel = ({ stage, boqGroup, autoOpen = false, embedded = false }) => {
  const stageId = stage.idExecutionPlanStageId;
  // embedded (current-step zone during Procurement) is always-open and chrome-free;
  // autoOpen opens immediately and, when no PO exists yet, drops straight into the
  // raise form with the vendor picker visible.
  const [open, setOpen] = useState(autoOpen || embedded);
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [raising, setRaising] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [lines, setLines] = useState([{ description: "", quantity: "", unit: "", vendorPrice: "", expectedDeliveryDate: "" }]);
  // View / edit an existing PO
  const [expandedPo, setExpandedPo] = useState(null);
  const [editPoId, setEditPoId] = useState(null);
  const [editLines, setEditLines] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [poRes, vRes] = await Promise.all([getPurchaseOrders(stageId), getVendorOptions(stageId)]);
      const list = poRes.purchaseOrders || [];
      setPos(list);
      setVendors(vRes || []);
      // Procurement step with no PO yet → surface the raise form (vendor picker) immediately.
      if (autoOpen && list.length === 0) { seedFromBoq(); setRaising(true); }
    } catch (e) { /* best-effort */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load();
    // `load` intentionally omitted — refetch only on open/close, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const seedFromBoq = () => {
    const seed = (boqGroup?.lines || []).map((l) => ({
      interiorDesignerBoqId: null,
      description: l.material || l.workItem || "",
      quantity: l.quantity ?? "",
      unit: l.unit || "",
      vendorPrice: "",
      expectedDeliveryDate: "",
    }));
    setLines(seed.length ? seed : [{ description: "", quantity: "", unit: "", vendorPrice: "", expectedDeliveryDate: "" }]);
  };

  // Selecting a vendor auto-fills the lines with the items that vendor WON (winning tender quote
  // lines) + their quoted unit price. Falls back to the BOQ seed when the vendor won nothing.
  const onVendorChange = async (vid) => {
    setVendorId(vid);
    if (!vid) return;
    try {
      const prefill = await getPoPrefill(stageId, Number(vid));
      if (Array.isArray(prefill) && prefill.length > 0) {
        setLines(
          prefill.map((l) => ({
            interiorDesignerBoqId: l.interiorDesignerBoqId ?? null,
            description: l.description || "",
            quantity: l.quantity ?? "",
            unit: l.unit || "",
            vendorPrice: l.vendorPrice ?? "",
            expectedDeliveryDate: dateFromLead(l.leadTimeDays),
          }))
        );
      } else {
        seedFromBoq();
      }
    } catch (e) {
      // best-effort — leave the current lines as-is
    }
  };

  const updateLine = (i, patch) => setLines((p) => p.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((p) => [...p, { description: "", quantity: "", unit: "", vendorPrice: "", expectedDeliveryDate: "" }]);
  const removeLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));

  const submitRaise = async () => {
    if (!vendorId) return toast.error("Pick a vendor");
    const payloadLines = lines
      .filter((l) => l.description && Number(l.quantity) > 0)
      .map((l) => ({
        interiorDesignerBoqId: l.interiorDesignerBoqId || null,
        description: l.description,
        quantity: Number(l.quantity),
        unit: l.unit || null,
        vendorPrice: l.vendorPrice === "" ? null : Number(l.vendorPrice),
        expectedDeliveryDate: l.expectedDeliveryDate || null,
      }));
    if (!payloadLines.length) return toast.error("Add at least one line with a quantity");
    setBusy(true);
    try {
      const res = await raisePurchaseOrder(stageId, {
        vendorId: Number(vendorId),
        lines: payloadLines,
      });
      setPos(res.purchaseOrders || []);
      setRaising(false);
      setVendorId(""); setLines([{ description: "", quantity: "", unit: "", vendorPrice: "", expectedDeliveryDate: "" }]);
      toast.success("Purchase order raised (draft)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to raise PO");
    } finally { setBusy(false); }
  };

  const doSend = async (po) => {
    setBusy(true);
    try {
      const res = await sendPurchaseOrder(po.purchaseOrderId);
      setPos(res.purchaseOrders || []);
      toast.success(`${po.poNumber} sent to vendor`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send PO");
    } finally { setBusy(false); }
  };

  const markDelivered = async (po) => {
    setBusy(true);
    try {
      const res = await updatePurchaseOrder(po.purchaseOrderId, {
        status: "DELIVERED",
        actualDeliveryDate: new Date().toISOString().slice(0, 10),
      });
      setPos(res.purchaseOrders || []);
      toast.success(`${po.poNumber} marked delivered`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update PO");
    } finally { setBusy(false); }
  };

  // --- view / edit an existing PO ---
  const toggleExpand = (poId) => setExpandedPo((p) => (p === poId ? null : poId));
  const startEdit = (po) => {
    setEditPoId(po.purchaseOrderId);
    setExpandedPo(po.purchaseOrderId);
    setEditLines((po.lines || []).map((l) => ({
      interiorDesignerBoqId: l.interiorDesignerBoqId ?? null,
      description: l.description || "",
      quantity: l.quantity ?? "",
      unit: l.unit || "",
      vendorPrice: l.vendorPrice ?? "",
      expectedDeliveryDate: l.expectedDeliveryDate ? String(l.expectedDeliveryDate).slice(0, 10) : "",
    })));
  };
  const cancelEdit = () => { setEditPoId(null); setEditLines([]); };
  const updateEditLine = (i, patch) => setEditLines((p) => p.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addEditLine = () => setEditLines((p) => [...p, { description: "", quantity: "", unit: "", vendorPrice: "", expectedDeliveryDate: "" }]);
  const removeEditLine = (i) => setEditLines((p) => p.filter((_, idx) => idx !== i));
  const saveEdit = async (po) => {
    const payloadLines = editLines
      .filter((l) => l.description && Number(l.quantity) > 0)
      .map((l) => ({
        interiorDesignerBoqId: l.interiorDesignerBoqId || null,
        description: l.description,
        quantity: Number(l.quantity),
        unit: l.unit || null,
        vendorPrice: l.vendorPrice === "" ? null : Number(l.vendorPrice),
        expectedDeliveryDate: l.expectedDeliveryDate || null,
      }));
    if (!payloadLines.length) return toast.error("Add at least one line with a quantity");
    setBusy(true);
    try {
      const res = await updatePurchaseOrder(po.purchaseOrderId, {
        lines: payloadLines,
      });
      setPos(res.purchaseOrders || []);
      cancelEdit();
      toast.success(po.status === "DRAFT" ? "Purchase order updated" : "PO updated — reverted to draft, re-send it");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update PO");
    } finally { setBusy(false); }
  };

  return (
    <div className={embedded ? "" : "mt-3 border-t border-slate-100 pt-2"}>
      {!embedded && (
        <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <FileText size={13} /> Purchase Orders{pos.length ? ` (${pos.length})` : ""}
        </button>
      )}

      {(embedded || open) && (
        <div className="mt-2 space-y-2">
          {loading ? (
            <div className="h-10 rounded-lg bg-slate-100 animate-pulse" />
          ) : (
            <>
              {pos.map((po) => {
                const isEditing = editPoId === po.purchaseOrderId;
                const isExpanded = expandedPo === po.purchaseOrderId || isEditing;
                const canEdit = ["DRAFT", "SENT", "ACKNOWLEDGED"].includes(po.status);
                return (
                <div key={po.purchaseOrderId} className="rounded-xl border border-slate-200 p-2.5 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => toggleExpand(po.purchaseOrderId)} className="text-slate-400 hover:text-slate-600" title="View items">
                      {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </button>
                    <span className="font-semibold text-slate-800">{po.poNumber}</span>
                    <span className="text-slate-500">{po.vendorName}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${PO_STATUS[po.status]}`}>{po.status.replace(/_/g, " ")}</span>
                    <span className="ml-auto font-medium text-slate-700">{fmtINR(po.totalAmount)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 pl-5">
                    {po.lines.length} line{po.lines.length === 1 ? "" : "s"} · expected {fmtDate(po.expectedDeliveryDate)}
                    {po.actualDeliveryDate && ` · delivered ${fmtDate(po.actualDeliveryDate)}`}
                  </div>

                  {/* Expanded read-only line items */}
                  {isExpanded && !isEditing && (
                    <div className="mt-2 pl-5 border-l-2 border-slate-100 space-y-1">
                      {(po.lines || []).map((l) => (
                        <div key={l.purchaseOrderLineId} className="flex items-center gap-2 text-[11px] text-slate-600">
                          <span className="flex-1 min-w-0 truncate">{l.description || "—"}</span>
                          <span className="w-16 text-right">{l.quantity}{l.unit ? ` ${l.unit}` : ""}</span>
                          <span className="w-20 text-right">{l.vendorPrice != null ? fmtINR(l.vendorPrice) : "—"}</span>
                          <span className="w-24 text-right font-medium text-slate-700">{l.lineTotal != null ? fmtINR(l.lineTotal) : "—"}</span>
                          <span className="w-24 text-right text-slate-500" title="Expected delivery">{l.expectedDeliveryDate ? fmtDate(l.expectedDeliveryDate) : "—"}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Inline edit form */}
                  {isEditing && (
                    <div className="mt-2 pl-5 space-y-1.5">
                      <p className="text-[11px] text-slate-400">Each line has its own expected delivery date (items can dispatch separately).</p>
                      {editLines.map((l, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <input value={l.description} onChange={(e) => updateEditLine(i, { description: e.target.value })}
                            placeholder="Material" className="flex-1 rounded-lg border border-slate-300 text-xs py-1.5 px-2" />
                          <input type="number" min="0" value={l.quantity} onChange={(e) => updateEditLine(i, { quantity: e.target.value })}
                            placeholder="Qty" className="w-16 rounded-lg border border-slate-300 text-xs py-1.5 px-2 text-right" />
                          <input value={l.unit} onChange={(e) => updateEditLine(i, { unit: e.target.value })}
                            placeholder="Unit" className="w-16 rounded-lg border border-slate-300 text-xs py-1.5 px-2" />
                          <input type="number" min="0" value={l.vendorPrice} onChange={(e) => updateEditLine(i, { vendorPrice: e.target.value })}
                            placeholder="₹ rate" className="w-20 rounded-lg border border-slate-300 text-xs py-1.5 px-2 text-right" />
                          <input type="date" value={l.expectedDeliveryDate} onChange={(e) => updateEditLine(i, { expectedDeliveryDate: e.target.value })}
                            className="w-32 rounded-lg border border-slate-300 text-xs py-1.5 px-2" title="Expected delivery" />
                          <button onClick={() => removeEditLine(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                        </div>
                      ))}
                      <button onClick={addEditLine} className="text-[11px] text-flamingo-600 hover:underline inline-flex items-center gap-1"><Plus size={11} /> Add line</button>
                      {po.status !== "DRAFT" && (
                        <p className="text-[11px] text-amber-600">Editing a sent PO reverts it to draft — you'll need to re-send it.</p>
                      )}
                      <div className="flex justify-end gap-2 pt-0.5">
                        <OutlineButton className="px-3 py-1 text-xs" onClick={cancelEdit}>Cancel</OutlineButton>
                        <PrimaryButton className="px-4 py-1 text-xs" onClick={() => saveEdit(po)} disabled={busy}>{busy ? "Saving…" : "Save changes"}</PrimaryButton>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-2 pl-5">
                    {po.pdfUrl && (
                      <button onClick={() => window.open(po.pdfUrl, "_blank", "noopener")} className="text-[11px] text-sky-600 hover:underline">PDF</button>
                    )}
                    {canEdit && !isEditing && (
                      <button onClick={() => startEdit(po)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:underline"><Pencil size={11} /> Edit</button>
                    )}
                    {po.status === "DRAFT" && (
                      <AsyncButton onClick={() => doSend(po)} disabled={busy} loadingText="Sending"
                        className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-flamingo-600 hover:underline"><Send size={11} /> Send to vendor</AsyncButton>
                    )}
                    {["SENT", "ACKNOWLEDGED", "PARTIALLY_DELIVERED"].includes(po.status) && (
                      <AsyncButton onClick={() => markDelivered(po)} disabled={busy} loadingText="Updating"
                        className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-emerald-600 hover:underline"><Truck size={11} /> Mark delivered</AsyncButton>
                    )}
                  </div>
                </div>
                );
              })}

              {raising ? (
                <div className="rounded-xl border border-flamingo-200 bg-flamingo-50/30 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <select value={vendorId} onChange={(e) => onVendorChange(e.target.value)}
                      className="flex-1 rounded-lg border border-slate-300 text-xs py-1.5 px-2 bg-white">
                      <option value="">Select vendor…</option>
                      {vendors.map((v) => (
                        <option key={v.vendorId} value={v.vendorId}>
                          {v.isPreferred ? "★ " : ""}{v.vendorName} — {v.matchedSkus} item(s){v.totalPrice != null ? `, ${fmtINR(v.totalPrice)}` : ""}{v.maxLeadTimeDays ? `, ${v.maxLeadTimeDays}d` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  {vendors.length === 0 && (
                    <p className="text-[11px] text-amber-600">No ranked vendors for this package's materials — enter lines manually.</p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase text-slate-400">Lines</span>
                    {boqGroup?.lines?.length > 0 && (
                      <button onClick={seedFromBoq} className="text-[10px] text-flamingo-600 hover:underline flex items-center gap-0.5"><Star size={10} /> Seed from BOQ</button>
                    )}
                  </div>
                  {lines.map((l, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })}
                        placeholder="Material" className="flex-1 rounded-lg border border-slate-300 text-xs py-1.5 px-2" />
                      <input type="number" min="0" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })}
                        placeholder="Qty" className="w-16 rounded-lg border border-slate-300 text-xs py-1.5 px-2 text-right" />
                      <input value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })}
                        placeholder="Unit" className="w-16 rounded-lg border border-slate-300 text-xs py-1.5 px-2" />
                      <input type="number" min="0" value={l.vendorPrice} onChange={(e) => updateLine(i, { vendorPrice: e.target.value })}
                        placeholder="₹ rate" className="w-20 rounded-lg border border-slate-300 text-xs py-1.5 px-2 text-right" />
                      <input type="date" value={l.expectedDeliveryDate} onChange={(e) => updateLine(i, { expectedDeliveryDate: e.target.value })}
                        className="w-32 rounded-lg border border-slate-300 text-xs py-1.5 px-2 bg-white" title="Expected delivery" />
                      <button onClick={() => removeLine(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                    </div>
                  ))}
                  <button onClick={addLine} className="text-[11px] text-flamingo-600 hover:underline inline-flex items-center gap-1"><Plus size={11} /> Add line</button>
                  <div className="flex justify-end gap-2 pt-1">
                    <OutlineButton className="px-3 py-1.5 text-xs" onClick={() => setRaising(false)}>Cancel</OutlineButton>
                    <PrimaryButton className="px-4 py-1.5 text-xs" onClick={submitRaise} disabled={busy}>{busy ? "Saving…" : "Raise PO"}</PrimaryButton>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setRaising(true); seedFromBoq(); }}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-flamingo-600 hover:text-flamingo-700">
                  <Plus size={13} /> Raise purchase order
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderPanel;
